import React from 'react';
import { getBuildLevel, isVehicleCertified } from '../../utils/buildLevel';
import { DiamondScoreBadge } from '../ui/DiamondScoreBadge';
import CertifiedBuildSeal from '../ui/CertifiedBuildSeal';
import { resolvePublicFileUrl } from '../../utils/imageUrls';
import '../VehicleCard.css';

export interface BuildCollectibleMiniProps {
  title: string;
  year: number;
  make: string;
  model: string;
  trim?: string | null;
  imageUrl?: string | null;
  modificationCount: number;
  certificationStatus?: string | null;
  rarityStars?: number | null;
  className?: string;
}

/**
 * Compact collectible plate for the build-page section rail —
 * same frame language as Discover VehicleCard, sized for the sticky nav.
 */
export const BuildCollectibleMini: React.FC<BuildCollectibleMiniProps> = ({
  title,
  year,
  make,
  model,
  trim,
  imageUrl,
  modificationCount,
  certificationStatus = null,
  rarityStars = null,
  className = '',
}) => {
  const buildLevel = getBuildLevel(modificationCount);
  const certified = isVehicleCertified(certificationStatus);
  const resolvedImage = imageUrl ? resolvePublicFileUrl(imageUrl) || imageUrl : null;
  const identity = [year, make, model, trim].filter(Boolean).join(' ');

  return (
    <div className={`vehicle-card build-collectible-mini ${className}`.trim()}>
      <div
        className={`pointer-events-none absolute -inset-px rounded-xl bg-gradient-to-br ${buildLevel.color} opacity-[0.12]`}
        aria-hidden
      />
      <div className={buildLevel.frameClass}>
        <div className="vehicle-card-frame__inner">
          {buildLevel.foilClass ? <div className={buildLevel.foilClass} aria-hidden /> : null}

          <div className="relative h-28 bg-gradient-to-b from-[#141414] to-[#0a0a0a] overflow-hidden">
            {resolvedImage ? (
              <img
                src={resolvedImage}
                alt=""
                className="w-full h-full object-cover vehicle-card-image"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center vehicle-card-no-image">
                <svg
                  className="w-10 h-10 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
            )}

            <div className="absolute top-2 right-2 z-20">
              <DiamondScoreBadge modCount={modificationCount} size="sm" />
            </div>

            {typeof rarityStars === 'number' && rarityStars > 0 ? (
              <div className="absolute top-2 left-2 z-20 rounded-md bg-[#0a0a0a]/85 border border-white/[0.08] px-1.5 py-0.5 text-[10px] font-semibold text-amber-200/90 tabular-nums">
                {rarityStars}★
              </div>
            ) : null}

            {certified ? (
              <div className="absolute bottom-2 left-2 z-20 scale-90 origin-bottom-left">
                <CertifiedBuildSeal status={certificationStatus} />
              </div>
            ) : null}
          </div>

          <div className="px-2.5 py-2.5 bg-[#0a0a0a]">
            <p className="text-sm font-semibold text-white leading-snug line-clamp-2 vehicle-card-title">
              {title}
            </p>
            <p className="mt-0.5 text-[11px] text-zinc-500 truncate">{identity}</p>
            <div className="mt-1.5 flex items-center justify-between gap-2">
              <span className="text-[11px] text-zinc-400 tabular-nums">
                {modificationCount} mod{modificationCount === 1 ? '' : 's'}
              </span>
              {buildLevel.showTierLabel ? (
                <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 border border-white/[0.08] rounded px-1.5 py-0.5">
                  {buildLevel.name}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuildCollectibleMini;
